import { z } from 'zod';
import { IdSchema } from './common.ts';
import { MoneySchema, addMoney, money, subtractMoney, type Money } from '../money.ts';
import { compareCostCodeNumbers, type CostCode, type CostCodeCategory } from './cost-code.ts';
import type { JobCostingRow } from '../../usecases/budget/job-costing.ts';

/**
 * The owner's copy of a budget row.
 *
 * A separate document rather than a filtered read of `budgetLines`, for the
 * reason `ownerChangeOrders` exists: Firestore rules are all-or-nothing per
 * document, and a budget row's `original` **is** the builder's cost. There
 * is no rule that lets an owner read the row while hiding that field. The
 * mirror carries owner pricing and no cost at all, which is a stronger
 * guarantee than a rule could express — the cost is not in the document to
 * leak.
 *
 * **The code's number, name and category are denormalised onto it**, which
 * is the other half of the same problem. An owner may not read `costCodes`
 * (`docs/PERMISSIONS.md` §4 gives the client `none`), so a mirror holding
 * only a `costCodeId` would be a page of money against opaque ids. The cost
 * code rule's own comment already names this as the right answer to that
 * class of problem — let the row carry what it needs rather than let a
 * reader join to a collection they are not meant to read.
 *
 * Denormalised text goes stale when a code is renamed, and that is
 * accepted: the recompute runs on every budget, change order and selection
 * write, so a rename shows up the next time any of the three moves. The
 * alternative — a fourth trigger on `costCodes` fanning out across every
 * job in the company — is a lot of writes to keep a label current.
 */
export const OwnerBudgetLineSchema = z.object({
  /** The cost code id, which is also the document id — as in `budgetLines`. */
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  costCodeId: IdSchema,
  /** `'3020'`. Denormalised — see above. */
  number: z.string().min(1).max(20),
  /** `'Contaminated soil removal'`. */
  name: z.string().min(1).max(120),
  /** `'Environmental'`, so the portal can group without reading categories. */
  categoryName: z.string().min(1).max(80),
  /** Where the category sits in the chart, so the owner's order matches the office's. */
  categoryOrder: z.number().int().min(0),
  /** What the owner was originally going to be charged for this code. */
  ownerOriginal: MoneySchema,
  /** Plus approved change orders and settled selections. Never a cost. */
  ownerRevised: MoneySchema,
  updatedAt: z.string().datetime(),
});
export type OwnerBudgetLine = z.infer<typeof OwnerBudgetLineSchema>;

export interface OwnerBudgetTotals {
  readonly original: Money;
  readonly revised: Money;
  /** Revised less original: what the owner's decisions have added. */
  readonly change: Money;
}

export interface OwnerBudgetGroup {
  readonly categoryName: string;
  readonly lines: readonly OwnerBudgetLine[];
  readonly totals: OwnerBudgetTotals;
}

/**
 * The owner's rows for a job's job-costing.
 *
 * Takes `JobCostingRow`s rather than recomputing, so the figure the owner
 * reads and the figure the office reads come from the same arithmetic. Two
 * implementations of `revised` would part company, and the day they did,
 * one of them would be in a customer's hands.
 *
 * A row whose cost code has been retired or deleted is **skipped**, not
 * guessed at: a budget row the mirror cannot name is better absent from the
 * owner's page than present as "Unknown code" with money against it. The
 * office's page still shows it, which is where somebody can act on it.
 */
export function ownerBudgetLinesFrom(
  rows: readonly JobCostingRow[],
  costCodes: readonly CostCode[],
  categories: readonly CostCodeCategory[],
  context: { readonly companyId: string; readonly jobId: string; readonly now: string },
): OwnerBudgetLine[] {
  const codeById = new Map(costCodes.map((code) => [code.id, code] as const));
  const categoryById = new Map(categories.map((category) => [category.id, category] as const));

  const lines: OwnerBudgetLine[] = [];
  for (const row of rows) {
    const code = codeById.get(row.costCodeId);
    if (!code) continue;
    const category = categoryById.get(code.categoryId);
    lines.push({
      id: row.costCodeId,
      companyId: context.companyId,
      jobId: context.jobId,
      costCodeId: row.costCodeId,
      number: code.number,
      name: code.name,
      // A code whose category has been deleted still has money against it.
      // `groupCostCodes` returns those as orphans rather than dropping
      // them, and this is the same answer in the owner's language.
      categoryName: category?.name ?? 'Other work',
      categoryOrder: category?.order ?? Number.MAX_SAFE_INTEGER,
      ownerOriginal: row.ownerOriginal,
      ownerRevised: row.ownerRevised,
      updatedAt: context.now,
    });
  }
  return lines;
}

export function ownerBudgetTotals(
  lines: readonly OwnerBudgetLine[],
  currency = 'USD',
): OwnerBudgetTotals {
  const original = lines.reduce(
    (running, line) => addMoney(running, line.ownerOriginal),
    money(0, currency),
  );
  const revised = lines.reduce(
    (running, line) => addMoney(running, line.ownerRevised),
    money(0, currency),
  );
  return { original, revised, change: subtractMoney(revised, original) };
}

/**
 * The owner's rows, grouped the way the office's chart is ordered.
 *
 * By `categoryOrder` then code number, so the owner's page reads in the
 * same order as the office's. Sorting by name would put the two documents
 * in different orders and make a phone call about "the third row" harder
 * than it needs to be.
 */
export function groupOwnerBudget(
  lines: readonly OwnerBudgetLine[],
  currency = 'USD',
): OwnerBudgetGroup[] {
  const groups = new Map<string, OwnerBudgetLine[]>();
  const order = new Map<string, number>();
  for (const line of lines) {
    const existing = groups.get(line.categoryName);
    if (existing) existing.push(line);
    else groups.set(line.categoryName, [line]);
    order.set(
      line.categoryName,
      Math.min(order.get(line.categoryName) ?? line.categoryOrder, line.categoryOrder),
    );
  }

  return [...groups.entries()]
    .sort(([a], [b]) => (order.get(a) ?? 0) - (order.get(b) ?? 0) || a.localeCompare(b))
    .map(([categoryName, group]) => {
      const sorted = [...group].sort((a, b) => compareCostCodeNumbers(a.number, b.number));
      return { categoryName, lines: sorted, totals: ownerBudgetTotals(sorted, currency) };
    });
}
